import { supabase } from '../supabase';
import { Tables } from '@/types/database.types';
import { handleError } from './db-utils';
import { getProjectById } from './projects';

interface CreateStatusUpdateOptions {
  type: 'accomplishment' | 'priority' | 'blocker' | 'dependency';
  content: string;
  weekOf?: string | null;
}

interface UpdateStatusUpdateOptions {
  type?: 'accomplishment' | 'priority' | 'blocker' | 'dependency';
  content?: string;
  weekOf?: string | null;
}

/**
 * Get all status updates for a project
 */
export const getProjectStatusUpdates = async (projectId: string): Promise<Tables<'status_updates'>[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check project access
    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const { data, error } = await supabase
      .from('status_updates')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) handleError(error, 'fetch status updates');
    return data || [];
  } catch (error) {
    handleError(error, 'fetch status updates');
    return [];
  }
};

/**
 * Create a new status update for a project
 */
export const createStatusUpdate = async (
  projectId: string,
  options: CreateStatusUpdateOptions
): Promise<Tables<'status_updates'>> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check project access
    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const { data, error } = await supabase
      .from('status_updates')
      .insert({
        project_id: projectId,
        type: options.type,
        content: options.content,
        week_of: options.weekOf || null,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) handleError(error, 'create status update');
    if (!data) throw new Error('No data returned from create');
    return data;
  } catch (error) {
    handleError(error, 'create status update');
    throw error;
  }
};

/**
 * Update a status update
 */
export const updateStatusUpdate = async (
  statusUpdateId: string,
  options: UpdateStatusUpdateOptions
): Promise<Tables<'status_updates'>> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    if (options.type !== undefined) updateData.type = options.type;
    if (options.content !== undefined) updateData.content = options.content;
    if (options.weekOf !== undefined) updateData.week_of = options.weekOf;

    const { data, error } = await supabase
      .from('status_updates')
      .update(updateData)
      .eq('id', statusUpdateId)
      .select()
      .single();

    if (error) handleError(error, 'update status update');
    if (!data) throw new Error('No data returned from update');
    return data;
  } catch (error) {
    handleError(error, 'update status update');
    throw error;
  }
};

/**
 * Delete a status update
 */
export const deleteStatusUpdate = async (statusUpdateId: string): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('status_updates')
      .delete()
      .eq('id', statusUpdateId);

    if (error) handleError(error, 'delete status update');
  } catch (error) {
    handleError(error, 'delete status update');
    throw error;
  }
};
